import React from 'react';
import { Link } from 'react-router-dom';
import { SatiLogo } from './Navigation';
import { useLanguage } from '../../context/LanguageContext';
import { categories } from '../../data/projects';
import { CONTACT_EMAIL, CONTACT_PHONE_DISPLAY, CONTACT_PHONE_TEL, CONTACT_TELEGRAM_URL } from '../../config/siteConstants';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t, language } = useLanguage();
  const categoryAnchors = categories.filter((c) => c.id !== 'all');

  return (
    <footer className="bg-charcoal-black text-ivory-mist" data-testid="footer">
      <div className="arch-container py-14 sm:py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
          {/* Logo & Description */}
          <div>
            <SatiLogo inverted className="mb-8" />
            <p className="text-[12px] text-ivory-mist/40 leading-[1.9] max-w-sm">
              {t.footer.description}
            </p>
            <div className="mt-6 flex flex-wrap gap-x-4 gap-y-1">
              {categoryAnchors.map((cat) => (
                <a
                  key={cat.id}
                  href={`/#${cat.id}`}
                  className="text-[10px] text-ivory-mist/35 hover:text-ivory-mist/70 tracking-[0.12em] uppercase transition-colors duration-300"
                >
                  {language === 'UA' ? cat.labelUA : cat.labelEN}
                </a>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="md:text-right">
            <span className="text-[10px] text-ivory-mist/30 tracking-[0.22em] uppercase block mb-6">
              {t.footer.connect}
            </span>
            <div className="space-y-2.5 sm:space-y-3 mb-8 sm:mb-10">
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="block text-[13px] text-ivory-mist/60 hover:text-ivory-mist transition-colors duration-500 min-h-[44px] flex items-center md:justify-end"
              >
                {CONTACT_EMAIL}
              </a>
              <a
                href={CONTACT_PHONE_TEL}
                className="block text-[13px] text-ivory-mist/60 hover:text-ivory-mist transition-colors duration-500 min-h-[44px] flex items-center md:justify-end"
              >
                {CONTACT_PHONE_DISPLAY}
              </a>
              <a
                href={CONTACT_TELEGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-[13px] text-ivory-mist/60 hover:text-ivory-mist transition-colors duration-500 min-h-[44px] flex items-center md:justify-end"
              >
                Telegram
              </a>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 md:justify-end">
              {[
                { name: 'Instagram', url: 'https://www.instagram.com/sati.architects/' },
                { name: 'Facebook', url: 'https://www.facebook.com/sati.architects/' },
              ].map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] text-ivory-mist/40 hover:text-ivory-mist tracking-[0.12em] uppercase transition-colors duration-500 min-h-[44px] inline-flex items-center"
                  data-testid={`footer-social-${social.name.toLowerCase()}`}
                >
                  {social.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-ivory-mist/[0.08]">
        <div className="arch-container py-5 sm:py-6 flex flex-col md:flex-row justify-between items-center gap-3 sm:gap-4">
          <p className="text-[10px] text-ivory-mist/30 tracking-[0.12em]">
            © {currentYear} SATI Architects
          </p>
          <div className="flex gap-8">
            <Link
              to="/privacy"
              className="text-[10px] text-ivory-mist/30 hover:text-ivory-mist/50 tracking-[0.12em] transition-colors duration-500"
              data-testid="footer-privacy"
            >
              {t.footer.privacy}
            </Link>
            <Link
              to="/terms"
              className="text-[10px] text-ivory-mist/30 hover:text-ivory-mist/50 tracking-[0.12em] transition-colors duration-500"
              data-testid="footer-terms"
            >
              {t.footer.terms}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
